import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled, TextField, Typography } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modals } from '../../../common/enum';
import { selectModalState, selectModalStateByName } from '../../../services/controllers/common/CommonSelector';
import { toggleModals } from '../../../services/controllers/common/CommonSlice';
import { FormulaActivityAttributes } from '../../../services/types/apiType';
import { useUpdateFormulaActivityMutation } from '../../../services/controllers/formula/FormulaQueries';

interface FormValues {
  colors: { name: string; quantity: number | string }[];
  chemicals: { name: string; quantity: number | string }[];
}

const UpdateFormulaActivity: React.FC = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector(selectModalStateByName(Modals.EDIT_FORMULA_ACTIVITY));
  const selectedData: FormulaActivityAttributes = useSelector(selectModalState);

  const { mutate: updateMutate, isLoading: updateLoading } = useUpdateFormulaActivityMutation();

  const onClose = () => {
    dispatch(toggleModals({ name: Modals.EDIT_FORMULA_ACTIVITY }));
  };

  const initialValues = useMemo(() => {
    return {
      colors: Object.entries(selectedData?.colors || {}).map(([name, quantity]) => ({ name, quantity })),
      chemicals: Object.entries(selectedData?.chemicals || {}).map(([name, quantity]) => ({ name, quantity })),
    };
  }, [selectedData]);

  const onSubmit = async (values: FormValues) => {
    if (!selectedData?.id) return;

    const formattedValues = {
      colors: values.colors.reduce((acc, color) => {
        acc[color.name] = Number.isNaN(Number(color.quantity)) ? 0 : Number(color.quantity);
        return acc;
      }, {} as Record<string, number>),
      chemicals: values.chemicals.reduce((acc, chemical) => {
        acc[chemical.name] = Number.isNaN(Number(chemical.quantity)) ? 0 : Number(chemical.quantity);
        return acc;
      }, {} as Record<string, number>),
    };

    updateMutate(
      { id: selectedData.id, value: formattedValues },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  if (!selectedData) return null;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Formik initialValues={initialValues} validateOnChange enableReinitialize onSubmit={onSubmit}>
        {({ handleSubmit, handleChange, handleBlur, values }) => {
          return (
            <>
              <DialogTitle id="alert-dialog-title">Chỉnh sửa hoạt động</DialogTitle>
              <DialogContent>
                <FormContainer
                  onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <ArrayFieldContainer>
                    <Typography sx={{ fontWeight: 500 }}>Màu sử dụng (G)</Typography>
                    <FieldArray name="colors">
                      {() => (
                        <Box>
                          {values.colors?.map((color: { name: string; quantity: string | number; id?: string }, index: number) => (
                            <ArrayItemContainer key={color.id || `color-${index}`}>
                              <ArrayInputGroup>
                                <Typography sx={{ width: '30%' }}>{color.name}</Typography>
                                <FormInput
                                  label=""
                                  name={`colors.${index}.quantity`}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={color.quantity || ''}
                                  variant="outlined"
                                />
                              </ArrayInputGroup>
                            </ArrayItemContainer>
                          ))}
                        </Box>
                      )}
                    </FieldArray>
                  </ArrayFieldContainer>
                  <ArrayFieldContainer>
                    <Typography sx={{ fontWeight: 500 }}>Hoá chất (Kg)</Typography>
                    <FieldArray name="chemicals">
                      {() => (
                        <Box>
                          {values.chemicals?.map((chemical: { name: string; quantity: string | number; id?: string }, index: number) => (
                            <ArrayItemContainer key={chemical.id || `chemical-${index}`}>
                              <ArrayInputGroup>
                                <Typography sx={{ width: '30%' }}>{chemical.name}</Typography>
                                <FormInput
                                  label=""
                                  name={`chemicals.${index}.quantity`}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={chemical.quantity || ''}
                                  variant="outlined"
                                />
                              </ArrayInputGroup>
                            </ArrayItemContainer>
                          ))}
                        </Box>
                      )}
                    </FieldArray>
                  </ArrayFieldContainer>
                </FormContainer>
              </DialogContent>
              <DialogActions>
                <Button disabled={updateLoading} variant="outlined" onClick={onClose}>
                  Huỷ bỏ
                </Button>
                <Button disabled={updateLoading} variant="contained" onClick={() => handleSubmit()}>
                  Lưu
                </Button>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

const FormInput = styled(TextField)(() => ({
  marginTop: 4,
  marginBottom: 4,
}));

const ArrayInputGroup = styled(Box)({
  display: 'flex',
  gap: 8,
  flex: 1,
  alignItems: 'center',

  '& > div': {
    flex: 1,
  },
});

const ArrayItemContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  '&:last-child': {
    marginBottom: 0,
  },
});

const FormContainer = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ArrayFieldContainer = styled(Box)({
  marginTop: 12,
  marginBottom: 12,
  padding: 16,
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: 8,
});

export default UpdateFormulaActivity;
